import type { LogbookClass, LogbookEntry, LogbookTimeMatrix } from '@/lib/db';

import { roundDecimalHours } from './time';

/**
 * CAA Form 1373's hour columns, derived from the flight record rather than
 * typed into.
 *
 * This is the middle of the three layers `docs/NZ-LOGBOOK.md` §6 sets out.
 * The pilot enters what actually happened — how long, how much of it after
 * dark, in what capacity, in what aeroplane — and 1373 asks for that same
 * time again sliced a different way: single or multi engine, day or night,
 * and then by capacity. Sixteen columns, every one of them a consequence of
 * four facts already stored.
 *
 * Deriving them rather than storing them is the whole point. A stored SE Day
 * PIC column is a number that can disagree with the day/night split and the
 * capacity split sitting beside it, and a logbook whose columns disagree is
 * one nobody can sign. Derived, it cannot: change the capacity, the class or
 * the night time and every affected column moves with it, in one pass, with
 * no chance of a stale cell left behind.
 *
 * What this module does *not* do is decide what counts toward a licence,
 * rating or currency requirement. These are the form's columns, which is a
 * question of arithmetic; creditable experience is a question about CAR 61
 * and belongs in `currency.ts`, where the rule references live.
 */

/** 1373's top-level split. The form prints these as two banks of columns, so nothing else does. */
export type CaaEngineGroup = 'single' | 'multi';

/**
 * Which bank of columns an entry's hours sit under.
 *
 * Only a multi-engine aeroplane is multi. Everything else — including the
 * classes 1373 has no column for at all, a helicopter or a glider — reads as
 * single, because a single-engine column is where a NZ pilot writes those by
 * hand on a form with nowhere else to put them, and silently dropping the
 * hours out of the table would be worse than putting them in the column the
 * paper form forces. The Class cell stays visible in the grid so the pilot
 * can see which airframe the hours in that column were flown in, and the
 * per-class totals on the totals page keep the distinction that 1373 itself
 * cannot express.
 */
export function caaEngineGroup(engineClass: LogbookClass): CaaEngineGroup {
  return engineClass === 'multi-engine-land' ? 'multi' : 'single';
}

/** The four capacities 1373 prints; `commandPractice` is what CAR 61 and the rest of this codebase call ICUS. */
export type CaaCapacity = 'dual' | 'pic' | 'coPilot' | 'commandPractice';

/**
 * A capacity's hours split across the day and night columns.
 *
 * `day + night` is always the capacity's whole time, exactly — see
 * `splitDayNight` for why that is arranged rather than hoped for.
 */
export interface DayNightHours {
  day: number;
  night: number;
}

/**
 * Split one capacity's hours into day and night in the same proportion as the
 * flight itself.
 *
 * The honest answer to a question the record cannot fully answer. A logbook
 * entry says *how much* of the flight was at night and *how much* was flown
 * as P1, but not which minutes were which — so a 2.0-hour flight with 0.5
 * night and 1.0 P1 genuinely does not know whether that P1 hour was before or
 * after last light. Pro rata is the only split that cannot be accused of
 * inventing a fact, and in the case that covers nearly every logbook row —
 * a flight that was entirely day or entirely night — it is not an
 * approximation at all but the exact answer.
 *
 * A pilot who flew the night portion in one capacity and the day portion in
 * another has flown what the form would call two lines, and splitting the
 * entry in two is the way to say so.
 *
 * The night side is rounded and the day side is then taken as the remainder,
 * so the two always add back to `hours` to the last stored place. Rounding
 * both independently loses a hundredth on a three-way split, and a column
 * that does not add up to its own total is the one thing a logbook may never
 * do.
 */
export function splitDayNight(hours: number, dayHr: number, nightHr: number): DayNightHours {
  if (hours === 0) {
    return { day: 0, night: 0 };
  }
  const flown = dayHr + nightHr;
  // Nothing to apportion against: an entry with capacity hours but no
  // day/night split at all is already flagged by `timeConsistencyIssues`, and
  // until the pilot fixes it the hours read as day rather than vanishing.
  if (flown <= 0 || nightHr <= 0) {
    return { day: roundDecimalHours(hours), night: 0 };
  }
  if (dayHr <= 0) {
    return { day: 0, night: roundDecimalHours(hours) };
  }
  const night = roundDecimalHours((hours * nightHr) / flown);
  return { day: roundDecimalHours(hours - night), night };
}

/** Every derived hour cell on one 1373 row, plus the hours the form has no column for. */
export interface Caa1373Row {
  /** Total flight time — `dayHr + nightHr`, the form's TOTAL column and the number every other cell is measured against. */
  totalHr: number;
  engineGroup: CaaEngineGroup;
  single: Record<CaaCapacity, DayNightHours>;
  multi: Record<CaaCapacity, DayNightHours>;
  /**
   * Hours with nowhere to go on the form.
   *
   * 1373 prints Co-Pilot and Command Practice under MULTI ENGINE only — a
   * deliberate feature of the form, not an oversight, since both describe a
   * crewed operation. A single-engine entry carrying either has hours that
   * the printed table cannot show, so they are surfaced here and flagged by
   * `timeConsistencyIssues` rather than quietly folded into Dual or PIC,
   * which would be this app deciding what the pilot flew.
   */
  unplacedHr: number;
}

const NO_HOURS: DayNightHours = { day: 0, night: 0 };

function zeroCapacities(): Record<CaaCapacity, DayNightHours> {
  return { dual: NO_HOURS, pic: NO_HOURS, coPilot: NO_HOURS, commandPractice: NO_HOURS };
}

/** The capacity fields, in the order 1373 prints them, paired with the stored field each reads. */
const CAPACITY_FIELD: Readonly<Record<CaaCapacity, keyof LogbookTimeMatrix>> = {
  dual: 'dualHr',
  pic: 'picHr',
  coPilot: 'coPilotHr',
  // ICUS under its CAA 1373 name. One concept, two vocabularies — see
  // `FlightRole` in `lib/db/types.ts`.
  commandPractice: 'commandPracticeHr',
};

/** Which capacities the form actually prints for each bank of columns. */
const CAPACITIES: Readonly<Record<CaaEngineGroup, readonly CaaCapacity[]>> = {
  single: ['dual', 'pic'],
  multi: ['dual', 'pic', 'coPilot', 'commandPractice'],
};

/**
 * One logbook entry as CAA 1373 would print it.
 *
 * Pure and cheap: no dates, no aircraft lookup, no database. The grid calls
 * this once per visible row and the totals page once per entry in a fold, so
 * it stays a function of the entry alone.
 */
export function caa1373Row(entry: LogbookEntry): Caa1373Row {
  const { time } = entry;
  const engineGroup = caaEngineGroup(time.engineClass);
  const printed = CAPACITIES[engineGroup];

  const filled = zeroCapacities();
  let unplacedHr = 0;
  for (const capacity of ['dual', 'pic', 'coPilot', 'commandPractice'] as const) {
    const hours = time[CAPACITY_FIELD[capacity]];
    if (typeof hours !== 'number' || hours === 0) {
      continue;
    }
    if (!printed.includes(capacity)) {
      unplacedHr = roundDecimalHours(unplacedHr + hours);
      continue;
    }
    filled[capacity] = splitDayNight(hours, time.dayHr, time.nightHr);
  }

  return {
    totalHr: roundDecimalHours(time.dayHr + time.nightHr),
    engineGroup,
    single: engineGroup === 'single' ? filled : zeroCapacities(),
    multi: engineGroup === 'multi' ? filled : zeroCapacities(),
    unplacedHr,
  };
}

/**
 * The capacity hours a role implies, for the entry form.
 *
 * The other direction of travel: `caa1373Row` turns stored hours into
 * columns, this turns the one choice the form asks for — "what were you
 * doing?" — into the stored hours. Which is what keeps §21's no-duplication
 * rule: the pilot types the flight time once and picks a role, and the
 * capacity split follows without a second number to keep in step.
 *
 * `'student'` and `'dual'` both mean dual received. They are separate roles
 * because they read differently in a logbook, and the same column because
 * 1373 has one.
 */
export function capacityHoursForRole(
  role: 'pic' | 'dual' | 'student' | 'co-pilot' | 'command-practice',
  totalHr: number,
): Pick<LogbookTimeMatrix, 'picHr' | 'commandPracticeHr' | 'dualHr' | 'coPilotHr'> {
  const blank = { picHr: 0, commandPracticeHr: 0, dualHr: 0, coPilotHr: 0 };
  switch (role) {
    case 'pic':
      return { ...blank, picHr: totalHr };
    case 'dual':
    case 'student':
      return { ...blank, dualHr: totalHr };
    case 'co-pilot':
      return { ...blank, coPilotHr: totalHr };
    case 'command-practice':
      return { ...blank, commandPracticeHr: totalHr };
  }
}

/**
 * The same sixteen cells, summed over any set of entries.
 *
 * §22 of the redesign brief asks for a total on every CAA column, not only
 * on flight time — a page, period, filter or career total that stops at
 * "hours flown" leaves the pilot adding up their own night PIC by hand,
 * which is the arithmetic the logbook exists to do.
 *
 * Rounded once at the end for the same reason `sumHours` in `columns.ts` is:
 * a thousand two-decimal numbers rounded as they are added drift from the
 * column they were added from.
 */
export function sumCaa1373(entries: readonly LogbookEntry[]): Caa1373Row {
  const single = zeroCapacities();
  const multi = zeroCapacities();
  let totalHr = 0;
  let unplacedHr = 0;

  for (const entry of entries) {
    const row = caa1373Row(entry);
    totalHr += row.totalHr;
    unplacedHr += row.unplacedHr;
    for (const capacity of ['dual', 'pic', 'coPilot', 'commandPractice'] as const) {
      single[capacity] = {
        day: single[capacity].day + row.single[capacity].day,
        night: single[capacity].night + row.single[capacity].night,
      };
      multi[capacity] = {
        day: multi[capacity].day + row.multi[capacity].day,
        night: multi[capacity].night + row.multi[capacity].night,
      };
    }
  }

  const round = (hours: DayNightHours): DayNightHours => ({
    day: roundDecimalHours(hours.day),
    night: roundDecimalHours(hours.night),
  });
  const roundAll = (bank: Record<CaaCapacity, DayNightHours>) => ({
    dual: round(bank.dual),
    pic: round(bank.pic),
    coPilot: round(bank.coPilot),
    commandPractice: round(bank.commandPractice),
  });

  return {
    totalHr: roundDecimalHours(totalHr),
    // A set of entries has no single engine group; the banks below carry the
    // split, and this field only ever describes one row. `'single'` is the
    // harmless answer, and nothing reads it off a summed row.
    engineGroup: 'single',
    single: roundAll(single),
    multi: roundAll(multi),
    unplacedHr: roundDecimalHours(unplacedHr),
  };
}
